import type { PuppyAssemblyNode } from './types'

/**
 * 克隆 JSON 兼容数据。
 *
 * @param value 源数据。
 * @returns 克隆后的数据。
 */
function cloneJsonCompatibleValue<T>(value: T | undefined): T | undefined {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value)) as T
}

/**
 * 克隆装配节点 meta 并移除运行时选择对象 ID。
 *
 * @param meta 源 hierarchy meta。
 * @returns 可写入 assembly.json 的 meta。
 */
export function cloneAssemblyNodeMeta(
  meta: PuppyAssemblyNode['meta'],
): PuppyAssemblyNode['meta'] {
  const cloned = cloneJsonCompatibleValue(meta)
  if (cloned) {
    /**
     * selectionObjectId / highlightTargets 都是导出前 Three.js 运行时 UUID。
     * display.glb 重新解析后这些 UUID 不再有效，导入端必须基于 assembly.json 的稳定
     * selectionAssemblyNodeId 与当前 runtime Object3D 重新建立选择代理和高亮目标。
     */
    delete cloned.selectionObjectId
    delete cloned.highlightTargets
    delete cloned.bodyHighlightTargetsById
    delete cloned.assemblyNodeId
    delete cloned.semanticKind
    delete cloned.hasGeometry
    delete cloned.hasDirectGeometry
    delete cloned.bodyCount
    delete cloned.bodyEntries
    delete cloned.bodyPrimitiveIndicesById
    delete cloned.runtimeWrapper
    // 未固定是装配组件的协议默认态。移动命令可能在运行时把默认态显式化为 false，
    // 持久化边界必须重新折叠默认值，避免无关位姿修改污染组件业务属性；固定态仍保存 true。
    if (cloned.isFixed === false) {
      delete cloned.isFixed
    }
    if (cloned.materialNames?.length === 0) {
      delete cloned.materialNames
    }
  }
  return cloned && Object.keys(cloned).length > 0 ? cloned : undefined
}
